import React, { useState, useCallback, useMemo, useEffect } from "react"
import { Paper, Stack, Box, useTheme } from "@mui/material"
import { useNavigate, useOutletContext } from "react-router-dom"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as TrashIcon } from "src/v2/assets/icons/trash-icon.svg"
import { ReactComponent as CopyIcon } from "src/v2/assets/icons/copy-icon.svg"
import { ReactComponent as PasteIcon } from "src/v2/assets/icons/paste-icon.svg"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import { eventosOrdinariosFields } from "./eventos-ordinarios-fields"
import { DynamicColumnTable } from "../componentes/dynamic-column-table"
import FormActions from "../../components/FormActions"
import { dispatchSnackbar } from "../../../../components/SnackBar/snackbar-bus"
import MotorEventosOrdinariosService from "src/services/motor-eventos-ordinarios-service"
import { mapEventosOrdinariosPayload, mapEventosOrdinariosToTable, toInt } from "../../utils/map-temporary-cadastro"
import Loader from "src/v2/components/Loader/loader"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import { useLocation } from "react-router-dom"

const TABLE_DATA_DEFAULT = [{}]
const MIN_COLS_ITEMS_TABLE = 7
const CSV_SEPARATOR = ";"

export const EventosOrdinariosPage = () => {
    const eventosService = useMemo(() => new MotorEventosOrdinariosService(), [])
    const theme = useTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const [tableData, setTableData] = useState(TABLE_DATA_DEFAULT)

    const handleCellChange = ({ rowIndex, colIndex, value }) => {
        const isEmptyValue = (v) => v == null || String(v).trim() === ""

        const isColumnEmpty = (column) => {
            if (!column) return true

            return eventosOrdinariosFields.every((field) =>
                isEmptyValue(column[field.key])
            )
        }

        const createEmptyColumn = () => {
            const emptyColumn = {}
            eventosOrdinariosFields.forEach((field) => {
                emptyColumn[field.key] = ""
            })
            return emptyColumn
        }

        const newData = [...tableData]

        while (newData.length < MIN_COLS_ITEMS_TABLE) {
            newData.push(createEmptyColumn())
        }

        if (!newData[colIndex]) {
            newData[colIndex] = createEmptyColumn()
        }

        if (rowIndex === -1) {
            newData[colIndex] = {
                ...newData[colIndex],
                [eventosOrdinariosFields[0].key]: value,
            }
        } else {
            const fieldKey = eventosOrdinariosFields[rowIndex + 1]?.key

            newData[colIndex] = {
                ...newData[colIndex],
                [fieldKey]: value,
            }
        }

        if (!isColumnEmpty(newData[newData.length - 1])) {
            newData.push(createEmptyColumn())
        }

        while (
            newData.length > MIN_COLS_ITEMS_TABLE &&
            isColumnEmpty(newData[newData.length - 1]) &&
            isColumnEmpty(newData[newData.length - 2])
        ) {
            newData.pop()
        }

        setTableData(newData)
    }

    const { parametrosInfo, eventosPublicado, setEventosPublicado } = useOutletContext() || {}
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    const idCaracteristica = parametrosInfo?.idCaracteristica
    const isPublished = eventosPublicado
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false)


    const fetchTableData = useCallback(async () => {
        if (!idCaracteristica) return

        try {
            const response = await eventosService.getEventosOrdinarios(idCaracteristica)
            const body = response?.body || response?.data

            if (body && body.length > 0) {
                setEventosPublicado(true)
                const formatted = mapEventosOrdinariosToTable(body)
                setTableData(formatted)
            }
        } catch (error) {
            console.error("Erro ao carregar dados", error)
        }

        try {
            const responseTemp = await eventosService.getTemporarioEventosOrdinarios(idCaracteristica)
            const bodyTemp = responseTemp?.body?.Eventos || responseTemp?.data?.Eventos

            if (bodyTemp && bodyTemp.length > 0) {
                setEventosPublicado(false)
                const formatted = mapEventosOrdinariosToTable(bodyTemp)
                setTableData(formatted)
            }
        } catch (error) {
            //
        }
    }, [idCaracteristica, eventosService])

    useEffect(() => {
        const eventos = location.state?.eventos

        if (eventos !== undefined) {
            const formatted = mapEventosOrdinariosToTable(eventos)
            setTableData(formatted)
            return
        }

        fetchTableData()
    }, [fetchTableData])

    const handleSave = async () => {
        const eventosMapeados = mapEventosOrdinariosPayload(tableData)

        if (eventosMapeados.length === 0) {
            dispatchSnackbar("Preencha ao menos um campo para salvar", "warning")
            return
        }

        const payload = {
            CETIP: parametrosInfo?.cetip || "",
            VersaoDocumento: toInt(parametrosInfo?.versaoDocumento) || 0,
            EventosOrdinarios: eventosMapeados
        }

        console.log("PAYLOAD:", payload)

        try {
            setIsSaving(true)
            const saveResponse = await eventosService.createCadastroTemporarioEventosOrdinarios(idCaracteristica, payload)
            const mensagem = saveResponse?.body?.mensagem ?? "Salvo com sucesso!"
            dispatchSnackbar(mensagem, "success")
            fetchTableData()
        } catch (error) {
            console.error(error)
            const data =
                error?.response?.body ||
                error?.response?.data ||
                error?.response?.text

            const eventos = data?.EventosOrdinarios
            if (Array.isArray(eventos)){
                const mensagens = eventos.flatMap(item=>
                    Object.entries(item).flatMap(([campo, erros]) =>
                        erros.map(msg => `${campo} : ${msg}`)
                    )
                )
                dispatchSnackbar(mensagens.join(" | "), "error")
                return
            }

            dispatchSnackbar("Erro ao salvar", "error")
        } finally {
            setIsSaving(false)
        }
    }

    const handlePublish = async () => {
        const eventosMapeados = mapEventosOrdinariosPayload(tableData)


        if (eventosMapeados.length === 0) {
            dispatchSnackbar("Preencha ao menos um campo para publicar", "warning")
            return
        }

        try {
            const hasProducaoId = eventosPublicado


            if (hasProducaoId) {
                await eventosService.updateCadastroEventosOrdinarios(
                    idCaracteristica,
                    eventosMapeados
                )
            } else {
                await eventosService.createCadastroEventosOrdinarios(
                    idCaracteristica,
                    eventosMapeados
                )
            }

            dispatchSnackbar(
                hasProducaoId
                    ? "Cadastro atualizado em produção com sucesso."
                    : "Cadastro publicado com sucesso.",
                "success"
            )
            setEventosPublicado(true)
            fetchTableData()

        } catch (error) {
            const data =
                error?.response?.body ||
                error?.response?.data ||
                error?.response?.text

            if (Array.isArray(data)){
                const mensagens = data.flatMap(item=>
                    Object.entries(item).flatMap(([campo, erros]) =>
                        erros.map(msg => `${campo} : ${msg}`)
                    )
                )
                dispatchSnackbar(mensagens.join(" | "), "error")
                return
            }

            const message =
                data?.detail ||
                data?.message ||
                error?.message ||
                "Falha ao publicar eventos."

            dispatchSnackbar(message, "error")
        } finally {
            setIsSaving(false)
            setPublishConfirmOpen(false)
        }
    }

    const handleDelete = useCallback(async () => {
        try {
            setIsDeleting(true)

            if (!idCaracteristica) return

            const isProducao = eventosPublicado

            if (isProducao) {
                await eventosService.deleteCadastroEventosOrdinarios(idCaracteristica)
            } else {
                await eventosService.deleteCadastroTemporarioEventosOrdinarios(idCaracteristica)
            }

            setTableData(TABLE_DATA_DEFAULT)
            setEventosPublicado(false)

            dispatchSnackbar(
                isProducao
                    ? "Cadastro deletado com sucesso."
                    : "Cadastro temporário deletado com sucesso.",
                "success"
            )

        } catch (error) {
            dispatchSnackbar("Erro ao deletar cadastro.", "error")
        } finally {
            setIsDeleting(false)
            setDeleteConfirmOpen(false)
        }
    }, [idCaracteristica, eventosPublicado, eventosService])

    const clearTable = () => {
        setTableData(TABLE_DATA_DEFAULT)
    }

    const escapeCsvValue = (value) => {
        if (value == null) return ""
        const stringValue = String(value)

        if (
            stringValue.includes(CSV_SEPARATOR) ||
            stringValue.includes('"') ||
            stringValue.includes("\n")
        ) {
            return `"${stringValue.replace(/"/g, '""')}"`
        }

        return stringValue
    }

    const copyTable = async () => {
        if (!tableData.length) return

        const headers = eventosOrdinariosFields.map((field) => field.key)
        const csv = [
            headers.join(CSV_SEPARATOR),
            ...tableData.map((row) =>
                headers.map((header) => escapeCsvValue(row[header])).join(CSV_SEPARATOR)
            ),
        ].join("\n")

        try {
            console.log(tableData)
            await navigator.clipboard.writeText(csv)
            dispatchSnackbar("Dados copiados para a área de transferência", "success")
        } catch (error) {
            dispatchSnackbar(`Erro ao copiar a tabela: ${error}`, "error")
        }
    }

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText()
            if (!text?.trim()) return

            const rows = text
                .trim()
                .split(/\r?\n/)
                .filter(r => r.trim() !== "")
                .slice(1)

            const keys = eventosOrdinariosFields.map((field) => field.key)
            const data = rows.map(row => {
                const cols = row.split("\t")
                const obj = {}

                cols.forEach((value, index) => {
                    const key = keys[index]
                    if (key) {
                        obj[key] = value.trim()
                    }
                })

                return obj
            })
            setTableData(data)
        } catch (error) {
            console.error("Erro ao ler clipboard:", error)
        }
    }

    return (
        <>
            {(isSaving || isDeleting) && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}

            <Paper sx={{ p: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <ButtonWithIcon icon={<TrashIcon />} size="md" onClick={clearTable}>
                        LIMPAR
                    </ButtonWithIcon>

                    <Stack direction="row" spacing={2}>
                        <ButtonWithIcon icon={<CopyIcon />} size="md" onClick={copyTable}>
                            COPIAR
                        </ButtonWithIcon>

                        <ButtonWithIcon icon={<PasteIcon />} size="md" onClick={handlePasteFromClipboard}>
                            COLAR
                        </ButtonWithIcon>
                    </Stack>
                </Stack>

                <Stack sx={{ mt: 2 }}>
                    <DynamicColumnTable
                        fields={eventosOrdinariosFields}
                        items={tableData}
                        onCellChange={handleCellChange}
                    />
                </Stack>

                <Box sx={{ mx: { xxxs: -3, md: -3, lg: 0 }, mt: 4 }}>
                    <FormActions
                        showBack
                        onBack={() => navigate(-1)}

                        onDelete={() => setDeleteConfirmOpen(true)}
                        deleteOpen={deleteConfirmOpen}
                        setDeleteOpen={setDeleteConfirmOpen}
                        onConfirmDelete={handleDelete}


                        showSave={!isPublished}
                        onSave={!isSaving ? handleSave : undefined}

                        onPublish={() => setPublishConfirmOpen(true)}
                        publishOpen={publishConfirmOpen}
                        setPublishOpen={setPublishConfirmOpen}
                        onConfirmPublish={handlePublish}
                        publishLabel={isPublished ? "SALVAR E PUBLICAR" : "PUBLICAR"}
                        publishIcon={<CheckIcon />}

                        theme={theme} />
                </Box>
            </Paper>
        </>
    )
}
